import React, { useMemo, useState } from 'react';
import { EditableTable, EditableTableColumn, EditableTableGroup } from 'src/presentations/components/organisms/editableTable/EditableTable';
import 'src/presentations/components/organisms/editableTable/EditableTable.css';
import { InvoiceMessageDto, ShipmentMessageDto } from 'src/generated/services/TFinancialApi';
import { ValidateNode } from 'src/use-cases/internalDashboard/useValidation';
import { ValidateInvoiceStep } from 'src/presentations/components/molecules/uploadInvoice/types';

interface ShipmentsTableProps {
  shipments: ShipmentMessageDto[];
  onUpdate: (shipments: ShipmentMessageDto[]) => void;
  validateJson?: ValidateNode<InvoiceMessageDto>;
  validateUpdatedJson?: ValidateNode<InvoiceMessageDto>;
  activeStep: ValidateInvoiceStep;
  ocrMessage: InvoiceMessageDto;
  isValidJson: boolean;
  setIsValidJson: (isInvalid: boolean) => void;
}

export const ShipmentsTable = ({
  shipments,
  onUpdate,
  validateJson,
  validateUpdatedJson,
  activeStep,
  ocrMessage,
  isValidJson,
  setIsValidJson,
}: ShipmentsTableProps) => {
  const [localShipments, setLocalShipments] = useState<ShipmentMessageDto[]>(shipments);
  const columns: EditableTableColumn[] = useMemo(
    () => [
      { key: 'shipmentNumber', label: 'Shipment Number', width: 160 },
      { key: 'internalTrackingNumber', label: 'Internal Tracking Number', width: 180 },
      { key: 'shipperName', label: 'Shipper Name', width: 160 },
      { key: 'consigneeName', label: 'Consignee Name', width: 160 },
      { key: 'placeOfLoading', label: 'Place of Loading', width: 160 },
      { key: 'placeOfDischarge', label: 'Place of Discharge', width: 160 },
      { key: 'transportationReferenceNumber', label: 'Transportation Reference Number', width: 200 },
      { key: 'carrier', label: 'Carrier', width: 120 },
      { key: 'numberOfPackages', label: 'Number of Packages', width: 120 },
      { key: 'numberOfPackagesDescription', label: 'Number of Packages Description', width: 180 },
      { key: 'serviceType', label: 'Service Type', width: 120 },
      { key: 'specialType', label: 'Special Type', width: 120 },
      { key: 'commodity', label: 'Commodity', width: 140 },
      { key: 'estimatedDepartureDate', label: 'Estimated Departure Date', width: 160 },
      { key: 'estimatedArrivalDate', label: 'Estimated Arrival Date', width: 160 },
      { key: 'masterBillOfLading', label: 'Master Bill of Lading', width: 180 },
      { key: 'houseBillOfLading', label: 'House Bill of Lading', width: 180 },
      { key: 'grossWeight', label: 'Gross Weight', width: 120 },
      { key: 'grossWeightInKg', label: 'Gross Weight in Kg', width: 120 },
      { key: 'chargeableWeight', label: 'Chargeable Weight', width: 120 },
      { key: 'chargeableWeightInKg', label: 'Chargeable Weight in Kg', width: 140 },
      { key: 'volumeInCbm', label: 'Volume in Cbm', width: 120 },
      { key: 'netWeight', label: 'Net Weight', width: 120 },
    ],
    []
  );

  // Helper to get validation for a shipment row/field
  const getShipmentValidation = (rowIdx: number, field: string) => {
    if (!(validateJson?.shipments as any)?.value) return { error: false, helperText: '' };
    const shipmentValidation = (validateJson.shipments as any).value[rowIdx];
    const shipmentUpdatedValidation = (validateUpdatedJson?.shipments as any)?.value?.[rowIdx];
    const fieldValidation = shipmentValidation?.[field];
    if (field === 'shipmentNumber' && isExistingShipmentNumber(rowIdx)) {
      return {
        error: true,
        helperText: 'Shipment number already exists',
      };
    }
    return {
      error: !!fieldValidation && fieldValidation.isValid === false,
      helperText: fieldValidation?.exception?.error || '',
      updatedError: !!shipmentUpdatedValidation?.[field]?.exception?.error,
      updatedHelperText: shipmentUpdatedValidation?.[field]?.exception?.error || '',
    };
  };

  const hasDuplicateShipmentNumbers = (shipmentsArr: ShipmentMessageDto[]) => {
    const seen = new Set<string>();
    for (const s of shipmentsArr) {
      const shipmentNum = s.shipmentNumber ?? '';
      if (seen.has(shipmentNum)) return true;
      seen.add(shipmentNum);
    }
    return false;
  };

  const isExistingShipmentNumber = (idx: number) => {
    const currentShipmentNumber = localShipments[idx]?.shipmentNumber ?? '';
    let count = 0;
    for (const element of localShipments) {
      if ((element?.shipmentNumber ?? '') === currentShipmentNumber) {
        count += 1;
      }
    }
    return count > 1;
  };

  // EditableTable expects a group, but for ungrouped just use one group with no key
  const group: EditableTableGroup = {
    label: '',
    rows: localShipments.map((row, idx) => {
      const rowWithValidation = { ...row };
      const originalRow = ocrMessage?.shipments?.[idx];
      columns.forEach((col) => {
        const v = getShipmentValidation(idx, col.key);
        rowWithValidation[`${col.key}__error`] = v.error;
        rowWithValidation[`${col.key}__helperText`] = v.helperText;
        rowWithValidation[`${col.key}__updatedError`] = v.updatedError;
        rowWithValidation[`${col.key}__originalValue`] = originalRow?.[col.key];
      });
      return rowWithValidation;
    }),
    expanded: true,
    onToggle: () => {},
    onAdd: () => {
      const newShipment = [
        ...localShipments,
        {
          shipmentNumber: '',
          internalTrackingNumber: '',
          shipperName: '',
          consigneeName: '',
          placeOfLoading: '',
          placeOfDischarge: '',
          transportationReferenceNumber: '',
          carrier: '',
          numberOfPackages: 0,
          numberOfPackagesDescription: '',
          serviceType: '',
          specialType: '',
          commodity: '',
          estimatedDepartureDate: '',
          estimatedArrivalDate: '',
          masterBillOfLading: '',
          houseBillOfLading: '',
          grossWeight: '',
          grossWeightInKg: 0,
          chargeableWeight: '',
          chargeableWeightInKg: 0,
          volumeInCbm: 0,
          netWeight: '',
          charges: [],
        } as ShipmentMessageDto,
      ];
      setLocalShipments(newShipment);
      onUpdate(newShipment);
      setIsValidJson(!hasDuplicateShipmentNumbers(newShipment));
    },
  };

  const handleDelete = (_groupKey: string, idx: number) => {
    const newShipments = localShipments.filter((_, i) => i !== idx);
    setLocalShipments(newShipments);
    onUpdate(newShipments);
    setIsValidJson(!hasDuplicateShipmentNumbers(newShipments));
  };

  const handleCellEdit = (_groupKey: string, idx: number, field: string, value: any) => {
    const newShipments = [...localShipments];
    newShipments[idx] = { ...newShipments[idx], [field]: value };
    setLocalShipments(newShipments);
    setIsValidJson(!hasDuplicateShipmentNumbers(newShipments));
  };

  const handleCellBlur = (_groupKey: string, idx: number, field: string, value: any) => {
    const newShipments = [...localShipments];
    newShipments[idx] = { ...newShipments[idx], [field]: value };
    setLocalShipments(newShipments);
    onUpdate(newShipments);
    setIsValidJson(!hasDuplicateShipmentNumbers(newShipments));
  };

  return (
    <div className='h-[400px] flex flex-col'>
      <EditableTable columns={columns} groups={[group]} onDelete={handleDelete} onCellEdit={handleCellEdit} onBlur={handleCellBlur} activeStep={activeStep} />
    </div>
  );
};
